import React, { useState } from "react";
import { api } from "./api";

function loginTarget(): string {
  if (typeof window === "undefined") return "/";
  const params = new URLSearchParams(window.location.search);
  const next = params.get("next");
  if (next?.startsWith("/") && !next.startsWith("//")) return next;
  return "/";
}

function loginReason(): string | null {
  if (typeof window === "undefined") return null;
  const reason = new URLSearchParams(window.location.search).get("reason");
  if (reason === "missing_session") return "Owner-Session fehlt. Bitte neu anmelden.";
  if (reason === "invalid_session") return "Owner-Session ist abgelaufen oder wurde widerrufen. Bitte neu anmelden.";
  if (reason === "unauthorized") return "API-Auth wurde nicht akzeptiert. Bitte Owner-Session neu starten.";
  return null;
}

export function LoginPage({ onLogin }: { onLogin?: () => void }): JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const reason = loginReason();
  const canSubmit = email.trim().length > 0 && password.length > 0 && !busy;

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.login(email.trim(), password);
      if (!res.ok) {
        setError("Anmeldung nicht bestätigt. Bitte E-Mail und Passwort prüfen.");
        return;
      }
      setPassword("");
      if (onLogin) {
        onLogin();
        return;
      }
      if (typeof window !== "undefined") {
        window.location.assign(loginTarget());
      }
    } catch {
      setError("Anmeldung nicht bestätigt. Bitte E-Mail und Passwort prüfen.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="simo-login-page" aria-label="SIMO Login">
      <section className="simo-login-shell">
        <div className="simo-login-brand" aria-hidden="true">
          <div className="simo-login-mark">
            <span />
          </div>
          <div>
            <strong>SIMO</strong>
            <small>Agent OS</small>
          </div>
        </div>

        <div className="simo-login-brief">
          <span>Owner Session</span>
          <h1>SIMO Login</h1>
          <p>
            Starte eine geschützte Browser-Session für Owner-Daten, Connectoren,
            Voice und Settings.
          </p>
          <dl>
            <div>
              <dt>Auth</dt>
              <dd>Session statt Browser-Popup</dd>
            </div>
            <div>
              <dt>Scope</dt>
              <dd>Owner-Daten erst nach Login</dd>
            </div>
            <div>
              <dt>Secrets</dt>
              <dd>Keine Token-Eingabe im Renderer</dd>
            </div>
          </dl>
        </div>

        <form className="simo-login-card simo-keyboard-safe-editor" onSubmit={submit} aria-label="Owner-Login">
          <div>
            <span className="simo-login-kicker">Anmelden</span>
            <h2>Owner-Zugang</h2>
            <p>Nutze deine SIMO Owner-E-Mail und dein Passwort.</p>
          </div>

          <label>
            <span>E-Mail</span>
            <input
              className="simo-keyboard-safe-field"
              type="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="owner@example.de"
            />
          </label>

          <label>
            <span>Passwort</span>
            <input
              className="simo-keyboard-safe-field"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Passwort"
            />
          </label>

          {(error || reason) && <div className="simo-login-error" role="alert">{error ?? reason}</div>}

          <button type="submit" disabled={!canSubmit}>
            {busy ? "Session wird gestartet..." : "Einloggen"}
          </button>

          <div className="simo-login-note">
            SIMO speichert nur die Browser-Session lokal. Owner-Token und Connector-Secrets
            werden hier nicht abgefragt.
          </div>
        </form>
      </section>
    </main>
  );
}
